import { css } from '@emotion/react';
import { useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { EmptySection } from '../../../components/EmptySection';
import { IFinanceRoundDataModel } from '../../../data-models/finance-round.data-model';
import { colors } from '../../../theme/colors';
import { mutedCaptionCss } from '../../CompanyProfiles/components/ActivityDrawer/activity-drawer-common.styles';
import { summaryPageBackground } from '../Summary/summary-layout.styles';
import { DealRoundCard } from './DealRoundCard';
import { selectedDealsRoundIdAtom } from './dealsSelection';
import { DealRoundDetail } from './DealRoundDetail';

export const containerCss = css`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 1rem;
  padding: 1rem;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: ${summaryPageBackground};
`;

export const sidebarCss = css`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  min-height: 0;
`;

// Same caption as the Activity drawer's round list, so the two lists read as one.
export const listHeaderCss = css`
  ${mutedCaptionCss};
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 0 0.25rem;
`;

export const listCountCss = css`
  color: ${colors.text.main};
`;

// 2px top padding keeps the first card's focus ring from being clipped by the scroll container.
export const listCss = css`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow-y: auto;
  padding: 2px 0.25rem 0.5rem;
`;

export const detailCss = css`
  overflow-y: auto;
  padding-right: 0.25rem;
`;

function sortRoundsByDateDesc(rounds: IFinanceRoundDataModel[]): IFinanceRoundDataModel[] {
  return [...rounds].sort((a, b) => {
    if (!a.roundDate) {
      return 1;
    }
    if (!b.roundDate) {
      return -1;
    }
    return new Date(b.roundDate).getTime() - new Date(a.roundDate).getTime();
  });
}

interface CompanyDealsViewProps {
  rounds: IFinanceRoundDataModel[];
  companyId: number;
}

/** Presentational master-detail view for the company-profile Deals tab. */
export function CompanyDealsView({ rounds, companyId }: CompanyDealsViewProps) {
  const sortedRounds = useMemo(() => sortRoundsByDateDesc(rounds), [rounds]);
  // Selection is shared so the Deal Docs sidebar can deep-link to a round.
  // Falls back to the most-recent round when null or not in this company's rounds.
  const [selectedRoundId, setSelectedRoundId] = useAtom(selectedDealsRoundIdAtom);
  const selectedRound = sortedRounds.find((round) => round.id === selectedRoundId) ?? sortedRounds[0];

  // The Activity drawer highlights whatever id this atom holds, so the fallback
  // has to be published rather than only rendered.
  useEffect(() => {
    if (selectedRound && selectedRound.id !== selectedRoundId) {
      setSelectedRoundId(selectedRound.id);
    }
  }, [selectedRound, selectedRoundId, setSelectedRoundId]);

  if (sortedRounds.length === 0) {
    return (
      <EmptySection
        title='No Deals Yet'
        message={
          'This company has no financing rounds recorded yet.\n' +
          'Add financing data in the company details to enable deal-level documents.'
        }
      />
    );
  }

  return (
    <div css={containerCss}>
      <div css={sidebarCss}>
        <div css={listHeaderCss}>
          <span>Financing Rounds</span>
          <span css={listCountCss}>{sortedRounds.length}</span>
        </div>
        <div css={listCss} role='group' aria-label='Financing rounds'>
          {sortedRounds.map((round) => (
            <DealRoundCard
              key={round.id}
              round={round}
              isSelected={round.id === selectedRound.id}
              onSelect={setSelectedRoundId}
            />
          ))}
        </div>
      </div>
      <div css={detailCss} role='region' aria-label='Round details'>
        <DealRoundDetail round={selectedRound} companyId={companyId} />
      </div>
    </div>
  );
}
